import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";

import { clientQueryKeys } from "./query-keys";

import { vacancyQueryKeys } from "@/features/vacancies/api/queryKeys";
import { instance } from "@/lib/axios/instance";
import { EditClientResponse } from "@/types/response/EditClientResponse";

type UpdateContactDetailsRequest = {
  firstName?: string;
  lastName?: string;
  infix?: string;
  emailAddress?: string;
  phoneNumber?: string;
  jobTitle?: string;
  tenantId?: string | null;
};

export function useUpdateContactDetailsMutation(
  clientId?: string,
  tenantId?: string | null
) {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<EditClientResponse>,
    AxiosError<{ title: string; detail: string; status?: number; extensions?: Record<string, unknown> }>,
    UpdateContactDetailsRequest
  >({
    mutationFn: (request: UpdateContactDetailsRequest) =>
      instance.put(`/api/v2/Clients/${clientId}/contact-details`, {
        firstName: request.firstName,
        lastName: request.lastName,
        infix: request.infix,
        emailAddress: request.emailAddress,
        phoneNumber: request.phoneNumber,
        jobTitle: request.jobTitle,
        tenantId: request.tenantId || tenantId,
      }),
    onSuccess: () => {
      // Invalidate all client queries
      queryClient.invalidateQueries({
        queryKey: clientQueryKeys.all,
      });
      // Invalidate specific client detail if we have clientId
      if (clientId) {
        queryClient.invalidateQueries({
          queryKey: clientQueryKeys.detail(clientId),
        });
      }
      // Invalidate vacancies as they may depend on client data
      queryClient.invalidateQueries({
        queryKey: vacancyQueryKeys.all,
      });
    },
  });
}
